import React, { createContext, useContext, useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Real Google OAuth login
  const handleGoogleLoginSuccess = async (tokenResponse) => {
    setIsLoading(true);
    try {
      // Get user info from Google using the access token
      const response = await axios.get(
        `https://www.googleapis.com/oauth2/v3/userinfo`,
        {
          headers: {
            Authorization: `Bearer ${tokenResponse.access_token}`,
          },
        }
      );

      const googleUserInfo = response.data;
      
      const userData = {
        id: googleUserInfo.sub,
        email: googleUserInfo.email,
        firstName: googleUserInfo.given_name || '',
        lastName: googleUserInfo.family_name || '',
        nickname: googleUserInfo.given_name || googleUserInfo.name,
        avatar: googleUserInfo.picture || googleUserInfo.given_name?.charAt(0).toUpperCase() || 'U',
        displayName: googleUserInfo.given_name || googleUserInfo.name,
        googleId: googleUserInfo.sub,
        picture: googleUserInfo.picture
      };
      
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Show success message
      setTimeout(() => {
        alert(`Welcome to conneCT, ${userData.displayName}!`);
      }, 100);
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Google login error:', error);
      alert('Failed to login with Google. Please try again.');
      return { success: false, error: 'Failed to login with Google' };
    } finally {
      setIsLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleLoginSuccess,
    onError: (error) => {
      console.error('Google OAuth error:', error);
      setIsLoading(false);
    },
  });

  const loginWithGoogle = async () => {
    setIsLoading(true);
    try {
      googleLogin();
      return { success: true };
    } catch (error) {
      setIsLoading(false);
      return { success: false, error: error.message };
    }
  };

  const login = async (email, password, rememberMe = false) => {
    setIsLoading(true);
    try {
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      const userData = {
        id: 1,
        email,
        firstName: 'Demo',
        lastName: 'User',
        nickname: 'DemoUser',
        avatar: null,
        displayName: 'DemoUser',
        rememberMe
      };
      
      setUser(userData);
      
      // Store session based on remember me preference
      if (rememberMe) {
        // Store for 30 days
        const expirationDate = new Date();
        expirationDate.setDate(expirationDate.getDate() + 30);
        const userWithExpiration = { ...userData, expiresAt: expirationDate.getTime() };
        localStorage.setItem('user', JSON.stringify(userWithExpiration));
      } else {
        // Store for session only
        sessionStorage.setItem('user', JSON.stringify(userData));
      }
      
      return { success: true, user: userData };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (userData) => {
    setIsLoading(true);
    try {
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      const newUser = {
        id: Date.now(), // Simple ID for demo
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        nickname: userData.nickname,
        birthday: userData.birthday,
        avatar: userData.nickname ? userData.nickname.charAt(0).toUpperCase() : userData.firstName.charAt(0).toUpperCase(),
        displayName: userData.nickname || userData.firstName
      };
      
      setUser(newUser);
      localStorage.setItem('user', JSON.stringify(newUser));
      return { success: true, user: newUser };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
  };

  // Check for existing session on app load
  React.useEffect(() => {
    // Check both localStorage (for remember me) and sessionStorage (for session only)
    let savedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
    
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        
        // Check if persistent session has expired
        if (userData.expiresAt && userData.expiresAt < Date.now()) {
          localStorage.removeItem('user');
        } else {
          setUser(userData);
        }
      } catch (error) {
        localStorage.removeItem('user');
        sessionStorage.removeItem('user');
      }
    }
  }, []);

  const value = {
    user,
    isLoading,
    login,
    signUp,
    loginWithGoogle,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};